/**
 * Route rules for app mode (the Twine iOS / Android app, see helpers/nativeApp).
 *
 * The single place that decides which routes are tab roots, which are
 * full-screen flows, and which pages the app never shows. AppTabBar,
 * AppHeader, the Android back button and the link interceptor all read
 * from here.
 */

export const FREELANCER_TABS = 'freelancer';
export const CLIENT_TABS = 'client';

// Profile tab root until TP-592 gives the profile tab its own screen.
const profilePath = (user) =>
  user && user.username ? `/${user.username}` : '/settings';

const TAB_SETS = {
  [FREELANCER_TABS]: (user) => [
    { id: 'jobs', label: 'Jobs', icon: 'jobs', path: '/ari' },
    {
      id: 'messages',
      label: 'Messages',
      icon: 'messages',
      path: '/messages',
      badge: 'unreadMessages',
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: 'applications',
      path: '/job-tracker',
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: 'profile',
      path: profilePath(user),
    },
  ],
  [CLIENT_TABS]: () => [
    { id: 'projects', label: 'Projects', icon: 'projects', path: '/ats' },
    // Opens the post flow, which is full screen: never a tab root itself.
    { id: 'post', label: 'Post a project', icon: 'post', path: '/post-a-job' },
    {
      id: 'messages',
      label: 'Messages',
      icon: 'messages',
      path: '/messages',
      badge: 'unreadMessages',
    },
    { id: 'account', label: 'Account', icon: 'account', path: '/settings' },
  ],
};

// Pushed screens fall back to the tab that owns them when there's no in-app
// history to go back to (e.g. opened from a link) and light up that tab.
const OWNING_TAB_RULES = {
  [FREELANCER_TABS]: [
    [/^\/messages(\/|$)/, 'messages'],
    [/^\/job-tracker(\/|$)/, 'applications'],
    [/^\/(settings|notifications|earnings)(\/|$)/, 'profile'],
  ],
  [CLIENT_TABS]: [
    [/^\/messages(\/|$)/, 'messages'],
    [/^\/(settings|notifications|payments)(\/|$)/, 'account'],
  ],
};

// No app chrome at all: these flows draw their own screens and their own
// way out (signup and onboarding steps, the post flow, the start screen).
const FULL_SCREEN_ROUTES = [
  /^\/app$/,
  /^\/onboarding(\/|$)/,
  /^\/signup-ari(\/|$)/,
  /^\/signup\/google$/,
  /^\/post\//,
  /^\/apply-for-verification$/,
];

// Shown under an AppHeader but never with the tab bar: signing in, an open
// conversation (the composer sits where the tab bar would), applying.
const NO_TAB_BAR_ROUTES = [
  /^\/signin$/,
  /^\/signup$/,
  /^\/auth\/code$/,
  /^\/messages\/[^/]+/,
];

// Pages the app never shows. Marketing pages open in the system browser;
// pricing is never opened at all (Apple 3.1.1 anti-steering).
const PURCHASE_ROUTES = [
  /^\/pricing(\/|$)/,
  /^\/manage\/briefs\/[^/]+\/pay(\/|$)/,
];

// '/' isn't here: in the app it redirects to the user's home tab.
const MARKETING_ROUTES = [
  /^\/enterprise(\/|$)/,
  /^\/howitworks(\/|$)/,
  /^\/whyusetwine(\/|$)/,
  /^\/hire-a-freelancer(\/|$)/,
  /^\/companies(\/|$)/,
  /^\/collaborate(\/|$)/,
  /^\/jobs\/categories(\/|$)/,
  /^\/find\//,
  /^\/get\//,
  /^\/tools\//,
  /^\/blog(\/|$)/,
  // Served by the marketing site (e.g. /freelance/one-page-website).
  /^\/freelance(\/|$)/,
  /^\/(aup|engagement-terms|platform-terms|copyright|privacy|terms)(\/|$)/,
];

// Client pages. Freelancers get them in the system browser, like marketing
// pages: the app is freelancer-first and has no client journey for them.
const CLIENT_ROUTES = [
  /^\/post-a-job$/,
  /^\/post(-manual)?\//,
  /^\/ats(\/|$)/,
  /^\/shortlist$/,
  /^\/onboarding\/client$/,
  /^\/(browse|search)$/,
];

// Absolute links to the production site. On a staging or local build they
// are rewritten to the build's own origin instead of leaving the app.
const PRODUCTION_HOSTS = /^(www\.)?twine\.net$/;

const PURCHASE_HOSTS = /(^|\.)(checkout|billing)\.stripe\.com$/;

const ROUTE_TITLES = [
  [/^\/signin$/, 'Log in'],
  [/^\/signup$/, 'Sign up'],
  [/^\/auth\/code$/, 'Enter code'],
  [/^\/ari\/job\//, 'Job'],
  [/^\/messages\/[^/]+/, 'Messages'],
  [/^\/settings(\/|$)/, 'Settings'],
  [/^\/notifications$/, 'Notifications'],
  [/^\/earnings$/, 'Earnings'],
  [/^\/payments$/, 'Payments'],
  [/^\/ats\/recent$/, 'Recent projects'],
  [/^\/ats\//, 'Project'],
  // Freelancers' portfolio projects, not client jobs.
  [/^\/projects\/manage$/, 'Portfolio projects'],
  [/^\/projects\/create$/, 'New portfolio project'],
  [/^\/projects\/[^/]+\/questions\//, 'Question'],
  [/^\/projects\//, 'Job'],
  [/^\/manage\/briefs(\/|$)/, 'Project'],
  [/^\/manage\/pitches(\/|$)/, 'Proposal'],
  [/^\/job-tracker\//, 'Application'],
  [/^\/saved-jobs$/, 'Saved jobs'],
  [/^\/shortlist$/, 'Shortlist'],
  [/^\/(browse|search)$/, 'Search'],
  [/^\/tag(\/|$)/, 'Search'],
  [/^\/report\/messages\//, 'Report'],
  [/^\/activate$/, 'Activate account'],
  [/^\/testimonials\//, 'Testimonial'],
  // Portfolio items, then any other single segment, which is a profile.
  [/^\/[^/]+\/c[^/]+/, 'Project'],
  [/^\/[^/]+$/, 'Profile'],
];

const matchesAny = (patterns, pathname) =>
  patterns.some((pattern) => pattern.test(pathname));

export function normalisePath(pathname = '/') {
  const path = pathname.split(/[?#]/)[0] || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

/** Which tab set a user gets in the app: freelancers, or client mode. */
export function getTabSetId(user) {
  if (!user) return null;
  return user.type === 'buyer' ? CLIENT_TABS : FREELANCER_TABS;
}

export function getAppTabs(user) {
  const setId = getTabSetId(user);
  return setId ? TAB_SETS[setId](user) : [];
}

export function isFullScreenRoute(pathname) {
  return matchesAny(FULL_SCREEN_ROUTES, normalisePath(pathname));
}

export function getTabRoot(pathname, user) {
  const path = normalisePath(pathname);
  return getAppTabs(user).find((tab) => tab.path === path) || null;
}

export function isTabRoot(pathname, user) {
  return Boolean(getTabRoot(pathname, user));
}

/** The tab a route belongs to: itself at a tab root, else by rule, else the first tab. */
export function getOwningTab(pathname, user) {
  const tabs = getAppTabs(user);
  if (!tabs.length) return null;
  const root = getTabRoot(pathname, user);
  if (root) return root;
  const path = normalisePath(pathname);
  const rule = (OWNING_TAB_RULES[getTabSetId(user)] || []).find(([pattern]) =>
    pattern.test(path)
  );
  const tabId = rule ? rule[1] : tabs[0].id;
  return tabs.find((tab) => tab.id === tabId) || tabs[0];
}

export function shouldShowTabBar(pathname, user) {
  if (!user || isFullScreenRoute(pathname)) return false;
  if (matchesAny(NO_TAB_BAR_ROUTES, normalisePath(pathname))) return false;
  return isTabRoot(pathname, user);
}

// Screens that pad for the notch and home indicator themselves (the start
// screen), so the content wrapper must not add its own safe-area padding.
const SELF_INSET_ROUTES = [/^\/app$/];

/**
 * Class for the wrapper around routed content in the app: clears the tab
 * bar on tab roots, the home indicator everywhere, and the notch on
 * full-screen flows (they have no AppTopBar to do it).
 */
export function getAppContentClassName(pathname, showTabBar) {
  if (matchesAny(SELF_INSET_ROUTES, normalisePath(pathname))) {
    return 'app-mode-content app-mode-content--self-inset';
  }
  if (showTabBar) return 'app-mode-content app-mode-content--tabs';
  if (isFullScreenRoute(pathname)) {
    return 'app-mode-content app-mode-content--full-screen';
  }
  return 'app-mode-content';
}

export function shouldShowAppHeader(pathname, user) {
  if (isFullScreenRoute(pathname)) return false;
  return !isTabRoot(pathname, user);
}

export function getRouteTitle(pathname) {
  const path = normalisePath(pathname);
  const match = ROUTE_TITLES.find(([pattern]) => pattern.test(path));
  return match ? match[1] : '';
}

export function isPurchaseRoute(pathname) {
  return matchesAny(PURCHASE_ROUTES, normalisePath(pathname));
}

export function isMarketingRoute(pathname) {
  return matchesAny(MARKETING_ROUTES, normalisePath(pathname));
}

export function isClientRoute(pathname) {
  return matchesAny(CLIENT_ROUTES, normalisePath(pathname));
}

/**
 * What the app does with a tapped link:
 * - 'block': purchase surfaces (pricing, Stripe Checkout / Portal). Never opened.
 * - 'browser': Twine marketing pages, and client pages for a freelancer,
 *   opened in the system browser.
 * - 'rewrite': an in-app page linked on www.twine.net from a staging or local
 *   build. Opened at the same path on the build's own origin.
 * - null: leave it to the WebView (in-app routes; Capacitor already sends
 *   other hosts to the system browser).
 */
export function classifyAppLink(href, currentOrigin, user) {
  let url;
  try {
    url = new URL(href, currentOrigin);
  } catch (e) {
    return null;
  }
  if (!/^https?:$/.test(url.protocol)) return null;
  if (PURCHASE_HOSTS.test(url.hostname)) return 'block';

  const isTwineHost =
    url.origin === currentOrigin || /(^|\.)twine\.net$/.test(url.hostname);
  if (!isTwineHost) return null;
  if (isPurchaseRoute(url.pathname)) return 'block';
  if (isMarketingRoute(url.pathname)) return 'browser';
  if (getTabSetId(user) === FREELANCER_TABS && isClientRoute(url.pathname)) {
    return 'browser';
  }
  if (url.origin !== currentOrigin && PRODUCTION_HOSTS.test(url.hostname)) {
    return 'rewrite';
  }
  return null;
}
